import PropTypes from 'prop-types';
import React from 'react';
// -*- mode: jsx;-*-

import { StandaloneSearchBoxes } from '../common/StandaloneSearchBoxes.js';
import LocalProjectsSection from './LocalProjectsSection.js';
import UrgentProjectsSection from './UrgentProjectsSection.tsx';
import { CalendarBannerSection } from './CalendarBannerSection.js';
import { useFavorites } from '../common/user/useFavorites.js';
import { Svg } from '../common/Svg.js';
import { TrackSectionView } from '../common/TrackSectionView.js';
import FollowContent from './FollowContent.js';
import { renderSavedSearches } from './renderSavedSearches.js';
import RecurringDonationSection from './RecurringDonationSection.js';

let ExistingDonorHome = function (props) {
    const {
        baseSearchURL, user, detectedLocation, filterCategories, extraFilters,
    } = props;

    const [followedProjects, setFollowedProjects] = React.useState();
    const [totalProjects, setTotalProjects] = React.useState();
    const { favorites, savedSearches, favoritesLoaded, searchesLoaded } = useFavorites();

    const { hasSearches, searchesContent } = renderSavedSearches(favoritesLoaded, favorites, searchesLoaded, savedSearches, baseSearchURL);

    let locationQuery = '';
    const geoCookie = JSON.parse(readCookie('geoLocation'));
    if (geoCookie && geoCookie.latitude && geoCookie.longitude) {
        locationQuery = '?centerLat=' + geoCookie.latitude + '&centerLng=' + geoCookie.longitude;
    } else if(geoCookie && geoCookie.zip) {
        locationQuery = '?startingZip=' + geoCookie.zip;
    }

    React.useEffect(() => {

        if (hasSearches !== undefined && totalProjects !== undefined) {
            const trackingData = {
                pageName,
                userId: user.userId,
                hasSavedSearches: hasSearches,
                hasFollows: favorites.schoolIds.length > 0 || favorites.teacherIds.length > 0 || favorites.projectIds.length > 0,
                totalFollowedProjectsWithUpdates: totalProjects,
            };

            if (totalProjects > 0) {
                trackingData.followedProjectIds = followedProjects.map(p => p.id).join(',');
            }

            /* global dcAnalytics */
            dcAnalytics.track('Return donor homepage loaded', trackingData);
        }
        // I'm intentionally omitting followedProjects here because we only want to send the first
        // page of project IDs the first time it loads. Including followedProjects would cause it to
        // trigger when paging through followed results, which is undesirable.
        // eslint-disable-next-line
    }, [hasSearches, totalProjects, user.userId]);

    return (
        <div className="homepage-donors">
            {(totalProjects == 0) // has follows
                ? '' :
                    <TrackSectionView sectionName="Search projects">
                        <div className="collapsed">
                            <h4>Welcome back, <a href={`${secureBaseURL}donor/${user.userId}`}>{user.salutation}!</a></h4>
                            <StandaloneSearchBoxes searchURL={baseSearchURL} baseSearchURL={baseSearchURL}
                                detectedLocation={detectedLocation}
                                filterCategories={filterCategories} extraFilters={extraFilters}/>
                        </div>
                    </TrackSectionView>
            }
            <TrackSectionView sectionName="Classroom essentials">
                <section className="classroom-essentials-callout">
                    <div className="container">
                        <div className="essentials-content">
                            <div className="supply-side">
                                <div className="animated-supply">
                                    <div></div>
                                    <div></div>
                                    <div></div>
                                </div>
                                <div className="badge"></div>
                            </div>
                            <div className="text-content">
                                <h1>Give the <span className="essentials-color">most requested</span> basic supplies</h1>
                                <a className="button-secondary small essentials" href="/donors/search.html?essentialsListsOnly=true">See teacher requests</a>
                            </div>
                        </div>
                    </div>
                </section>
            </TrackSectionView>

            <TrackSectionView sectionName="Map section">
                    <div className={`map-callout ${(totalProjects == 0) ? ' larger' : ''}`}>
                        <div className="container">
                            <div className="fluid-container">
                                <div className="school-callout">
                                    <h2>Give back to your community</h2>
                                    <a className="button-primary" href={`${baseURL}donors/map.html${locationQuery}`} tabIndex="0" role="button" aria-label="View map of local requests">See local schools</a>
                                    <div className="map-bg"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </TrackSectionView>
            <div className={`container all-activity ${(totalProjects == 0) ? 'onboard' : ''}`}>
                {(totalProjects == 0 && !hasSearches) // has no follows and no searches
                    ? <div className="recent-activity no-searches-no-follows">
                        <FollowContent baseSearchURL={baseSearchURL} user={user} favoritesLoaded={favoritesLoaded}
                            favorites={favorites} followedProjects={followedProjects} totalProjects={totalProjects}
                            setTotalProjects={setTotalProjects} setFollowedProjects={setFollowedProjects} />
                    </div>
                    : <div className="results-container"> {/* has follows, but no active searches */}
                        <div className="recent-activity has-searches-no-follows">
                            <FollowContent baseSearchURL={baseSearchURL} user={user} favoritesLoaded={favoritesLoaded}
                                favorites={favorites} followedProjects={followedProjects} totalProjects={totalProjects}
                                setTotalProjects={setTotalProjects} setFollowedProjects={setFollowedProjects} />
                        </div>
                        <div className="right-side">
                            <div className={`saved-searches ${hasSearches ? '' : 'zero'}`}>
                                {searchesContent}
                            </div>
                            {(totalProjects !== undefined && totalProjects > 5) &&
                            <TrackSectionView sectionName="Calendar banner section">
                                <CalendarBannerSection />
                            </TrackSectionView>
                            }
                        </div>
                    </div>
                }
            </div>
            {(totalProjects == 0) // moving search down when no follows
                ? <div>
                    <TrackSectionView sectionName="Search projects">
                        <div className="search-projects lower">
                            <div className="search-directive">Try typing in the name of a teacher in your life or a school you went to and <span className="follow-example"><Svg icon="bookmark"/>Follow</span> them. </div>
                            <StandaloneSearchBoxes searchURL={baseSearchURL} baseSearchURL={baseSearchURL}
                                detectedLocation={detectedLocation}
                                filterCategories={filterCategories} extraFilters={extraFilters}/>
                        </div>
                    </TrackSectionView>
                </div>
                : ''}
            {(userObj && userObj.hasActiveRecurringDonation === false) ? <RecurringDonationSection {...props} /> : null}
            {(totalProjects !== undefined && totalProjects < 5) &&
                <div className="more-projects">
                    <TrackSectionView sectionName="Calendar banner section">
                        <CalendarBannerSection />
                    </TrackSectionView>
                    <div className="etc-projects">
                        <LocalProjectsSection {...props} />
                        <UrgentProjectsSection {...props} />
                    </div>
                </div>
            }
        </div>
    );
};

ExistingDonorHome.propTypes = {
    baseSearchURL: PropTypes.string.isRequired,
    loadProjects: PropTypes.func.isRequired,
    getProjects: PropTypes.func.isRequired,
    maxNumRecentDonations: PropTypes.number.isRequired,
    user: PropTypes.object.isRequired,
    filterCategories: PropTypes.array,
    extraFilters: PropTypes.array,
    detectedLocation: PropTypes.object,
};
ExistingDonorHome = React.memo(ExistingDonorHome);
export default ExistingDonorHome;
